import { LockKeyhole, LockOpen } from "lucide-react";
import React, { useRef, useState } from "react";
import { useSelector } from "react-redux";

import { IgTooltip } from "@intelligems/ig-theme";
import { ToggleButton } from "@mui/material";
import {
  VARIATION_BUTTON_MAX_WIDTH,
  VARIATION_BUTTON_MIN_WIDTH,
} from "@src/features/experienceBuilder/constants";
import { ToggleButtonGroup } from "@src/features/experienceBuilder/toolbar/ToggleButtonGroup";
import { useDispatch } from "@src/store";
import { ExperienceBuilderAiSelector } from "@src/store/slices/experienceBuilder/ai";
import {
  ExperienceBuilderAction,
  ExperienceBuilderSelector,
} from "@src/store/slices/experienceBuilder";
import { selectedVariations } from "@src/store/slices/variations";

interface VariationButtonProps {
  variationId: string;
  name: string;
  icon?: React.ReactNode;
  disabled?: boolean;
}

const VariationButton = ({
  variationId,
  name,
  icon,
  disabled,
}: VariationButtonProps) => {
  const textRef = useRef<HTMLSpanElement>(null);
  const [showTooltip, setShowTooltip] = useState(false);

  const handleMouseEnter = () => {
    if (textRef.current) {
      const isOverflowing =
        textRef.current.scrollWidth > textRef.current.clientWidth;
      setShowTooltip(isOverflowing);
    }
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  return (
    <IgTooltip title={showTooltip ? name : ""}>
      <ToggleButton
        value={variationId}
        size="small"
        disabled={disabled}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        sx={{
          maxWidth: VARIATION_BUTTON_MAX_WIDTH,
          minWidth: 0,
          "& > svg": { flexShrink: 0 },
        }}
      >
        {icon}
        <span
          ref={textRef}
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {name}
        </span>
      </ToggleButton>
    </IgTooltip>
  );
};

export const TestGroupSwitcher = () => {
  const dispatch = useDispatch();
  const variations = useSelector(selectedVariations);
  const selectedVariationId = useSelector(
    ExperienceBuilderSelector.getVariationId
  );
  const controlGroupLocked = useSelector(
    ExperienceBuilderSelector.getControlGroupLocked
  );
  const isAiBusy = useSelector(ExperienceBuilderAiSelector.getIsAiBusy);

  const getControlIcon = () => {
    if (controlGroupLocked) {
      return <LockKeyhole size={14} />;
    }
    return <LockOpen size={14} />;
  };

  const handleChange = (_event: unknown, value: string) => {
    if (value !== null) {
      void dispatch(ExperienceBuilderAction.switchVariationId(value));
    }
  };

  const buttons = variations.map((variation) => (
    <VariationButton
      key={variation.id}
      variationId={variation.id}
      name={variation.name}
      icon={variation.isControl ? getControlIcon() : undefined}
      disabled={isAiBusy}
    />
  ));

  return (
    <IgTooltip
      title={isAiBusy ? "AI is busy, please wait..." : ""}
      wrapperStyle={{ display: "flex", minWidth: 0 }}
    >
      <ToggleButtonGroup
        buttons={buttons}
        value={selectedVariationId}
        control={{
          exclusive: true,
          onChange: handleChange,
        }}
        sx={{
          minWidth: 0,
          "& > span": {
            display: "inline-flex",
            minWidth: VARIATION_BUTTON_MIN_WIDTH,
          },
        }}
      />
    </IgTooltip>
  );
};
